import React, { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { sl } from "date-fns/locale";
import Select from "react-select";
import axios from "../axios";
import "../styles/select-styles.css";
import { useWidth } from "../hooks/useWidth";

registerLocale("sl", sl);

const OdsotnostiPage = () => {
    const { isLower } = useWidth(640);
    const [monthlyDataState, setMonthlyData] = useState([]);
    // Absence types definition
    const absenceTypes = {
        dopust: { color: "bg-blue-200", label: "D", fullName: "Dopust" },
        bolniska: { color: "bg-red-200", label: "B", fullName: "Bolniška" },
        nega: { color: "bg-purple-200", label: "N", fullName: "Nega" },
        kasnejsi_prihod: {
            color: "bg-yellow-200",
            label: "KP",
            fullName: "Kasnejši prihod",
        },
        predcasni_odhod: {
            color: "bg-orange-200",
            label: "PO",
            fullName: "Predčasni odhod",
        },
        ostalo: { color: "bg-gray-200", label: "O", fullName: "Ostalo" },
    };

    // States
    const [podatki, setPodatki] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newAbsence, setNewAbsence] = useState({
        name: "",
        startDate: "",
        endDate: "",
        type: "",
        description: "",
        zoranOdobril: false,
    });
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedAbsence, setSelectedAbsence] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedAbsence, setEditedAbsence] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
            2,
            "0"
        )}`;
    });
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedEmployeesForSummary, setSelectedEmployeesForSummary] =
        useState([]);
    const [selectedAbsenceTypes, setSelectedAbsenceTypes] = useState(
        Object.keys(absenceTypes)
    );
    const [yearData, setYearData] = useState([]); // Initialize yearData as an empty array
    const [selectAllWasClicked, setSelectAllWasClicked] = useState(false);

    // List of all employees
    const allEmployees = [
        { value: "AP", label: "AP - Alja Pirkmajer" },
        { value: "BT", label: "BT - Barbara Trilar" },
        { value: "EČ", label: "EČ - Esad Čeman" },
        { value: "JC", label: "JC - Jana Caserman" },
        { value: "JD", label: "JD - Janez Dragar" },
        { value: "KK", label: "KK - Ksenija Kus" },
        { value: "KM", label: "KM - Katarina Mrđa" },
        { value: "MJ", label: "MJ - Metka Junc" },
        { value: "MK", label: "MK - Mateja Kladivnik" },
        { value: "ND", label: "ND - Nenad Dončič" },
        { value: "NM", label: "NM - Naja Malešič" },
        { value: "NMR", label: "NMR - Nataša Mulej" },
        { value: "RL", label: "RL - Rok Lujanac" },
        { value: "RLM", label: "RLM - Roman Lesjak" },
        { value: "SĐ", label: "SĐ - Sanja Đukič" },
        { value: "TD", label: "TD - Tea Drozg" },
        { value: "TP", label: "TP - Tina Poljak" },
        { value: "UT", label: "UT - Urška Tomšič" },
        { value: "ZP", label: "ZP - Zdenko Peršolja" },
        { value: "S", label: "S - Sara" },
        { value: "T", label: "T - Tjaša" },
        { value: "L", label: "L - Lara" },
        { value: "MG", label: "MG - Miha Grancer" },
        // Praktikanti
        { value: "JP", label: "JP - Juš Penko" },
    ];

    // Add Select All option to employees
    const employees = [
        { value: "SELECT_ALL", label: "Izberi vse", isSelectAll: true },
        ...allEmployees,
    ];

    // Custom Option component with checkbox
    const CustomOption = (props) => {
        const { data, isSelected, innerRef, innerProps } = props;
        return (
            <div
                ref={innerRef}
                {...innerProps}
                className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                    isSelected ? "bg-blue-50" : ""
                }`}
            >
                <input
                    name="employeeSingleCheck"
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                        if (data.isSelectAll && isSelected) {
                            setSelectedEmployeesForSummary([]);
                        } else if (
                            selectedEmployeesForSummary.length ===
                                allEmployees.length &&
                            isSelected
                        ) {
                            setSelectedEmployeesForSummary(
                                selectedEmployeesForSummary.filter(
                                    (emp) => emp !== data.value
                                )
                            );
                        }
                    }}
                    className="w-4 h-4 mr-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                    htmlFor="employeeSingleCheck"
                    className={`${
                        data.isSelectAll ? "font-semibold text-blue-600" : ""
                    }`}
                >
                    {data.label}
                </label>
            </div>
        );
    };

    // Handle employee selection with Select All logic
    const handleEmployeeSelectionChange = (selected) => {
        if (!selected) {
            setSelectedEmployeesForSummary([]);
            setSelectAllWasClicked(false);
            return;
        }
        const selectedValues = selected.map((s) => s.value);
        const hasSelectAll = selectedValues.includes("SELECT_ALL");
        const regularEmployees = allEmployees.map((emp) => emp.value);
        const regularSelectedValues = selectedValues.filter(
            (val) => val !== "SELECT_ALL"
        );

        const currentlyHasAll =
            selectedEmployeesForSummary.length === regularEmployees.length;

        if (hasSelectAll) {
            // Select All was clicked - select all employees
            setSelectedEmployeesForSummary(regularEmployees);
            setSelectAllWasClicked(true);
        } else if (
            selectAllWasClicked &&
            currentlyHasAll &&
            regularSelectedValues.length === 0
        ) {
            // Select All was deselected - keep all current employees selected
            setSelectedEmployeesForSummary(selectedEmployeesForSummary);
            setSelectAllWasClicked(false);
        } else {
            // Normal individual selection/deselection
            setSelectedEmployeesForSummary(regularSelectedValues);
            setSelectAllWasClicked(false);
        }
    };

    // Convert absence types to options for Select
    const absenceTypeOptions = Object.entries(absenceTypes).map(
        ([value, { fullName }]) => ({
            value,
            label: fullName,
        })
    );

    // Function to get days in month
    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };

    // Function to format month name with year
    const formatMonthDisplay = (dateString) => {
        const [year, month] = dateString.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleString("sl-SI", { month: "long", year: "numeric" });
    };

    // Function to check if date is weekend
    const isWeekend = (year, month, day) => {
        const date = new Date(year, month, day);
        return date.getDay() === 0 || date.getDay() === 6;
    };

    // Slovenian holidays
    const slovenianHolidays = [
        { day: 1, month: 0, name: "Novo leto" }, // Jan 1
        { day: 2, month: 0, name: "Novo leto" }, // Jan 2
        { day: 8, month: 1, name: "Prešernov dan" }, // Feb 8
        { day: 27, month: 3, name: "Dan upora proti okupatorju" }, // Apr 27
        { day: 1, month: 4, name: "Praznik dela" }, // May 1
        { day: 2, month: 4, name: "Praznik dela" }, // May 2
        { day: 25, month: 5, name: "Dan državnosti" }, // Jun 25
        { day: 15, month: 7, name: "Marijino vnebovzetje" }, // Aug 15
        { day: 31, month: 9, name: "Dan reformacije" }, // Oct 31
        { day: 1, month: 10, name: "Dan spomina na mrtve" }, // Nov 1
        { day: 25, month: 11, name: "Božič" }, // Dec 25
        { day: 26, month: 11, name: "Dan samostojnosti in enotnosti" }, // Dec 26
    ];

    // Function to check if date is holiday
    const isHoliday = (year, month, day) => {
        return slovenianHolidays.some(
            (holiday) => holiday.day === day && holiday.month === month
        );
    };

    // Function to get holiday name
    const getHolidayName = (year, month, day) => {
        const holiday = slovenianHolidays.find(
            (holiday) => holiday.day === day && holiday.month === month
        );
        return holiday ? holiday.name : "";
    };

    // Function to get employee display name
    const getEmployeeDisplayName = (employeeCode) => {
        const employee = employees.find((emp) => emp.value === employeeCode);
        return employee ? employeeCode : employeeCode; // Just show the code (AP, BT, etc.)
    };

    // Format date for display
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("sl-SI", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate || !endDate) return "";
        const days = calculateDays(startDate, endDate);
        return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(
            endDate
        )} (${days} ${days === 1 ? "dan" : "dni"})`;
    };

    // Function to navigate to previous month
    const goToPreviousMonth = () => {
        const [year, month] = selectedMonth.split("-").map(Number);
        const date = new Date(year, month - 2); // -2 because month is 1-based
        setSelectedMonth(
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
                2,
                "0"
            )}`
        );
    };

    // Function to navigate to next month
    const goToNextMonth = () => {
        const [year, month] = selectedMonth.split("-").map(Number);
        const date = new Date(year, month);
        setSelectedMonth(
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
                2,
                "0"
            )}`
        );
    };

    // Function to go to current month
    const goToCurrentMonth = () => {
        const now = new Date();
        setSelectedMonth(
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
                2,
                "0"
            )}`
        );
    };

    // Month navigation component
    const MonthNavigation = () => (
        <div className="flex items-center justify-between p-4 mb-4 bg-white rounded-lg shadow-sm">
            <button
                onClick={goToPreviousMonth}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 focus:outline-none"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            <h2 className="font-semibold text-center text-gray-800 sm:text-lg">
                {formatMonthDisplay(selectedMonth)}
            </h2>

            <button
                onClick={goToNextMonth}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 focus:outline-none"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>
        </div>
    );

    // Calculate days between dates (inclusive)
    const calculateDays = (startDate, endDate) => {
        if (!startDate || !endDate) return null;
        const start = new Date(startDate);
        const end = new Date(endDate);
        // Reset hours to midnight to ensure accurate day calculation
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    };

    const fetchData = async () => {
        if (!selectedMonth) {
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(
                `/api/absences?month=${selectedMonth}`
            );

            // Process the data
            const processedData = response.data.map((absence) => ({
                ...absence,
                startDate: absence.startDate,
                endDate: absence.endDate,
                zoranOdobril:
                    absence.zoranOdobril === true ||
                    absence.zoranOdobril === "Da",
            }));

            setPodatki(processedData);
            setError(null);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Napaka pri pridobivanju podatkov");
            setPodatki([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchYearData = async () => {
        try {
            const response = await axios.get("/api/odsotnosti");
            setYearData(response.data || []); // Ensure we always set an array
        } catch (error) {
            console.error("Error fetching year data:", error);
            setYearData([]); // Set empty array on error
        }
    };

    // Function to format date to YYYY-MM-DD without timezone issues
    const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !newAbsence.name ||
            !dateRange[0] ||
            !dateRange[1] ||
            !newAbsence.type
        ) {
            alert("Prosim izpolnite vsa obvezna polja");
            return;
        }

        try {
            // Find the selected employee
            const selectedEmployee = employees.find(
                (emp) => emp.value === newAbsence.name
            );
            if (!selectedEmployee) {
                throw new Error("Selected employee not found");
            }

            // Get the employee code (e.g., 'AP', 'BT')
            const employeeCode = selectedEmployee.value;

            // Format dates to YYYY-MM-DD
            const formattedStartDate = formatDateForAPI(dateRange[0]);
            const formattedEndDate = formatDateForAPI(dateRange[1]);

            const absenceData = {
                ...newAbsence,
                name: employeeCode, // Use the employee code
                startDate: formattedStartDate,
                endDate: formattedEndDate,
            };

            const response = await axios.post("/api/absences", absenceData);

            // Add the newly created absence to the list
            setPodatki((prev) => [...prev, response.data]);

            // Reset form
            setNewAbsence({
                name: "",
                startDate: "",
                endDate: "",
                type: "",
                description: "",
                zoranOdobril: false,
            });
            setDateRange([null, null]);
            setError(null);

            // Refresh the data
            await fetchData();
            await fetchYearData();
        } catch (error) {
            console.error("Error adding absence:", error);
            setError("Napaka pri dodajanju odsotnosti");
        }
    };

    const handleCellClick = (absence, currentDate) => {
        setSelectedAbsence({ ...absence, currentDate });
        setEditedAbsence(absence);
        setIsEditing(false);
    };

    const handleCloseModal = () => {
        setSelectedAbsence(null);
        setEditedAbsence(null);
        setIsEditing(false);
    };

    const handleEdit = () => {
        setIsEditing(true);
        setDateRange([
            new Date(editedAbsence.startDate),
            new Date(editedAbsence.endDate),
        ]);
    };

    const handleSaveEdit = async () => {
        try {
            const response = await axios.put(
                `/api/absences/${editedAbsence.id}`,
                editedAbsence
            );

            // Update the list with the edited absence
            setPodatki((prevPodatki) =>
                prevPodatki.map((item) =>
                    item.id === editedAbsence.id ? editedAbsence : item
                )
            );
            setSelectedAbsence(editedAbsence);
            setIsEditing(false);
            setError(null); // Clear any previous errors
        } catch (error) {
            console.error("Error updating absence:", error);
            setError("Napaka pri posodabljanju odsotnosti");
        }
    };

    const handleDelete = async () => {
        if (
            window.confirm(
                "Ali ste prepričani, da želite izbrisati to odsotnost?"
            )
        ) {
            try {
                await axios.delete(`/api/absences/${selectedAbsence.id}`);

                // Remove the deleted absence from the list
                setPodatki((prevPodatki) =>
                    prevPodatki.filter((item) => item.id !== selectedAbsence.id)
                );
                setSelectedAbsence(null);
                setError(null); // Clear any previous errors
            } catch (error) {
                console.error("Error deleting absence:", error);
                setError("Napaka pri brisanju odsotnosti");
            }
        }
    };

    const handleDeleteDay = async (date) => {
        if (
            window.confirm(
                "Ali ste prepričani, da želite izbrisati odsotnost na ta dan?"
            )
        ) {
            try {
                const response = await axios.put(
                    `/api/absences/day/${selectedAbsence.id}`,
                    {
                        date,
                    }
                );

                setPodatki((prevPodatki) =>
                    prevPodatki.map((item) =>
                        item.id === selectedAbsence.id
                            ? {
                                  ...item,
                                  odjavljeniDnevi:
                                      response.data.odjavljeniDnevi,
                              }
                            : item
                    )
                );
                setSelectedAbsence(null);
                setError(null); // Clear any previous errors
            } catch (error) {
                console.error("Error deleting absence:", error);
                setError("Napaka pri brisanju odsotnosti");
            }
        }
    };

    // Check if a date falls within an absence period
    const isDateInAbsence = (date, absence) => {
        const checkDate = new Date(date);
        const startDate = new Date(absence.startDate);
        const endDate = new Date(absence.endDate);

        // Reset hours to midnight for accurate comparison
        checkDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        return checkDate >= startDate && checkDate <= endDate;
    };

    // Parse odjavljeniDnevi string and check if date is in it
    const isDateInOdjavljeniDnevi = (date, odjavljeniDnevi) => {
        if (!odjavljeniDnevi || typeof odjavljeniDnevi !== "string")
            return false;

        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);

        // Parse dates from string format "17. 07. 2025, 18. 07. 2025"
        const dateStrings = odjavljeniDnevi.split(",").map((str) => str.trim());

        return dateStrings.some((dateStr) => {
            // Parse "17. 07. 2025" format
            const parts = dateStr.split(".").map((part) => part.trim());
            if (parts.length !== 3) return false;

            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]);
            const year = parseInt(parts[2]);

            if (isNaN(day) || isNaN(month) || isNaN(year)) return false;

            const parsedDate = new Date(year, month - 1, day);
            parsedDate.setHours(0, 0, 0, 0);

            return parsedDate.getTime() === checkDate.getTime();
        });
    };

    // Render calendar table
    const renderCalendarTable = () => (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead>
                    <tr>
                        <th className="sticky left-0 z-10 px-4 py-2 text-sm font-medium tracking-wider text-left text-gray-500 uppercase bg-gray-50">
                            Ime
                        </th>
                        {[
                            ...Array(
                                getDaysInMonth(
                                    new Date(selectedMonth).getFullYear(),
                                    new Date(selectedMonth).getMonth()
                                )
                            ),
                        ].map((_, index) => {
                            const day = index + 1;
                            const currentDate = new Date(selectedMonth);
                            currentDate.setDate(day);
                            const isWeekendDay = isWeekend(
                                currentDate.getFullYear(),
                                currentDate.getMonth(),
                                day
                            );
                            const isHolidayDay = isHoliday(
                                currentDate.getFullYear(),
                                currentDate.getMonth(),
                                day
                            );

                            return (
                                <th
                                    key={day}
                                    className={`px-2 py-2 text-center text-sm font-medium text-gray-500 w-8 ${
                                        isHolidayDay
                                            ? "bg-red-50"
                                            : isWeekendDay
                                            ? "bg-gray-100"
                                            : "bg-gray-50"
                                    }`}
                                    title={
                                        isHolidayDay
                                            ? getHolidayName(
                                                  currentDate.getFullYear(),
                                                  currentDate.getMonth(),
                                                  day
                                              )
                                            : ""
                                    }
                                >
                                    {day}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {employees.slice(1).map((employee) => (
                        <tr key={employee.value} className="hover:bg-gray-50">
                            <td className="sticky left-0 px-4 py-2 text-sm font-medium text-gray-900 bg-white whitespace-nowrap">
                                {isLower ? employee.value : employee.label}
                            </td>
                            {[
                                ...Array(
                                    getDaysInMonth(
                                        new Date(selectedMonth).getFullYear(),
                                        new Date(selectedMonth).getMonth()
                                    )
                                ),
                            ].map((_, dayIndex) => {
                                const day = dayIndex + 1;
                                const currentDate = new Date(selectedMonth);
                                currentDate.setDate(day);
                                const isWeekendDay = isWeekend(
                                    currentDate.getFullYear(),
                                    currentDate.getMonth(),
                                    day
                                );
                                const isHolidayDay = isHoliday(
                                    currentDate.getFullYear(),
                                    currentDate.getMonth(),
                                    day
                                );

                                // Find absence for this day
                                const absence = podatki.find(
                                    (a) =>
                                        a.name === employee.value &&
                                        isDateInAbsence(currentDate, a)
                                );

                                let cellContent = "";
                                let cellClass = "";
                                if (absence) {
                                    // Check if this date is in odjavljeniDnevi - if so, use white background
                                    const isInOdjavljeniDnevi =
                                        isDateInOdjavljeniDnevi(
                                            currentDate,
                                            absence.odjavljeniDnevi
                                        );
                                    const type = absenceTypes[absence.type];
                                    cellContent =
                                        isWeekendDay ||
                                        isHolidayDay ||
                                        isInOdjavljeniDnevi
                                            ? ""
                                            : type.label;

                                    cellClass = isInOdjavljeniDnevi
                                        ? "bg-white"
                                        : type.color;
                                }

                                return (
                                    <td
                                        key={`${employee.value}-${day}`}
                                        className={`px-2 py-2 text-center text-xs border-r ${
                                            absence
                                                ? isWeekendDay
                                                    ? "bg-gray-100"
                                                    : isHolidayDay
                                                    ? "bg-red-50"
                                                    : cellClass
                                                : isHolidayDay
                                                ? "bg-red-50"
                                                : isWeekendDay
                                                ? "bg-gray-100"
                                                : ""
                                        } cursor-pointer hover:opacity-75`}
                                        title={
                                            absence
                                                ? `${
                                                      absence.description ||
                                                      absence.type
                                                  }`
                                                : ""
                                        }
                                        onClick={
                                            absence &&
                                            !(
                                                isWeekendDay ||
                                                isHolidayDay ||
                                                isDateInOdjavljeniDnevi(
                                                    currentDate,
                                                    absence.odjavljeniDnevi
                                                )
                                            )
                                                ? () =>
                                                      handleCellClick(
                                                          absence,
                                                          currentDate
                                                      )
                                                : undefined
                                        }
                                    >
                                        {cellContent}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // Function to get month abbreviation
    const getMonthAbbreviation = (month) => {
        const date = new Date(2024, month, 1);
        return date.toLocaleString("sl", { month: "short" });
    };

    // Function to calculate absences by month and type
    const calculateMonthlyAbsences = () => {
        const monthlyData = {};

        // Only process selected employees, or show a message if none selected
        const employeesToProcess =
            selectedEmployeesForSummary.length > 0
                ? selectedEmployeesForSummary
                : [];

        employeesToProcess.forEach((employeeValue) => {
            const employee = employees.find(
                (emp) => emp.value === employeeValue
            );
            if (employee) {
                monthlyData[employee.value] = {
                    name: employee.label,
                    months: Array(12)
                        .fill(null)
                        .map(() => {
                            const monthData = {};
                            selectedAbsenceTypes.forEach((type) => {
                                monthData[type] = 0;
                            });
                            return monthData;
                        }),
                };
            }
        });

        // Process year data if it exists and is an array
        if (Array.isArray(yearData)) {
            yearData.forEach((absence) => {
                if (!absence || !absence.startDate || !absence.endDate) return;

                // Find the employee by their initials (e.g., "AP - Alja Pirkmajer" -> "AP")
                const employeeInitials = absence.ime?.split("-")[0]?.trim();
                if (!employeeInitials || !monthlyData[employeeInitials]) return;
                if (!selectedAbsenceTypes.includes(absence.tip)) return;

                const startDate = new Date(absence.startDate);
                const endDate = new Date(absence.endDate);

                // Skip if not in selected year
                if (
                    startDate.getFullYear() !== selectedYear &&
                    endDate.getFullYear() !== selectedYear
                )
                    return;

                // Calculate days in each month
                let currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                    // Only count if in selected year
                    if (currentDate.getFullYear() === selectedYear) {
                        const month = currentDate.getMonth();
                        monthlyData[employeeInitials].months[month][
                            absence.tip
                        ]++;
                    }
                    // Move to next day
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            });
        }

        setMonthlyData(monthlyData);
    };

    const handleEditStartDateChange = (date) => {
        if (!date) {
            setEditedAbsence((prev) => ({
                ...prev,
                startDate: null,
                days: null,
            }));
            return;
        }
        const adjustedDate = new Date(
            date.getTime() - date.getTimezoneOffset() * 60000
        );
        const formattedDate = adjustedDate.toISOString().split("T")[0];

        setEditedAbsence((prev) => {
            const days =
                formattedDate && prev.endDate
                    ? calculateDays(formattedDate, prev.endDate)
                    : null;
            return {
                ...prev,
                startDate: formattedDate,
                days,
            };
        });
    };

    const handleEditEndDateChange = (date) => {
        if (!date) {
            setEditedAbsence((prev) => ({
                ...prev,
                endDate: null,
                days: null,
            }));
            return;
        }
        const adjustedDate = new Date(
            date.getTime() - date.getTimezoneOffset() * 60000
        );
        const formattedDate = adjustedDate.toISOString().split("T")[0];

        setEditedAbsence((prev) => {
            const days =
                prev.startDate && formattedDate
                    ? calculateDays(prev.startDate, formattedDate)
                    : null;
            return {
                ...prev,
                endDate: formattedDate,
                days,
            };
        });
    };

    const getTextColor = (type) => {
        if (!absenceTypes[type]) {
            return "bg-black";
        }
        const absenceTypeForTextColor =
            absenceTypes[type].fullName.toLowerCase();
        switch (absenceTypeForTextColor) {
            case "dopust":
                return "bg-blue-200";
            case "bolniška":
                return "bg-red-200";
            case "nega":
                return "bg-purple-200";
            case "predčasni odhod":
                return "bg-orange-200";
            case "kasnejši prihod":
                return "bg-yellow-200";
            case "ostalo":
                return "bg-gray-200";
            default:
                return "bg-black";
        }
    };

    useEffect(() => {
        fetchData();
        fetchYearData();
    }, [selectedMonth]);

    useEffect(() => {
        calculateMonthlyAbsences();
    }, [selectedEmployeesForSummary, selectedAbsenceTypes, yearData]);

    if (loading) {
        return <div className="p-4">Nalaganje podatkov...</div>;
    }

    if (error) {
        return <div className="p-4 text-red-500">{error}</div>;
    }

    return (
        <div className="p-4">
            {/* Form Section */}
            <div className="p-6 mb-8 bg-white rounded-lg shadow-md">
                <h2 className="mb-4 text-xl font-semibold">Nova odsotnost</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="relative mb-4">
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Oseba *
                            </label>
                            <Select
                                value={employees.find(
                                    (emp) => emp.value === newAbsence.name
                                )}
                                onChange={(selected) =>
                                    setNewAbsence((prev) => ({
                                        ...prev,
                                        name: selected?.value || "",
                                    }))
                                }
                                options={employees}
                                className="basic-select"
                                classNamePrefix="select"
                                placeholder="Izberi osebo..."
                                menuPortalTarget={document.querySelector(
                                    "body"
                                )}
                                menuPosition="fixed"
                                menuPlacement="auto"
                            />
                        </div>

                        <div className="relative mb-4">
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Tip odsotnosti *
                            </label>
                            <Select
                                value={absenceTypeOptions.find(
                                    (type) => type.value === newAbsence.type
                                )}
                                onChange={(selected) =>
                                    setNewAbsence((prev) => ({
                                        ...prev,
                                        type: selected?.value || "",
                                    }))
                                }
                                options={absenceTypeOptions}
                                className="basic-select"
                                classNamePrefix="select"
                                placeholder="Izberi vrsto odsotnosti..."
                                menuPortalTarget={document.querySelector(
                                    "body"
                                )}
                                menuPosition="fixed"
                                menuPlacement="auto"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Obdobje *
                            </label>
                            <div className="mt-1 space-y-2">
                                <DatePicker
                                    selected={
                                        dateRange[0]
                                            ? new Date(
                                                  dateRange[0].getTime() +
                                                      dateRange[0].getTimezoneOffset() *
                                                          60000
                                              )
                                            : null
                                    }
                                    onChange={(date) => {
                                        if (date) {
                                            // Set time to noon to avoid timezone issues
                                            date.setHours(12, 0, 0, 0);
                                        }
                                        setDateRange([date, dateRange[1]]);
                                    }}
                                    dateFormat="dd.MM.yyyy"
                                    placeholderText="Izberi začetni datum"
                                    className="w-full p-2 border rounded"
                                    locale="sl"
                                />
                                <DatePicker
                                    selected={
                                        dateRange[1]
                                            ? new Date(
                                                  dateRange[1].getTime() +
                                                      dateRange[1].getTimezoneOffset() *
                                                          60000
                                              )
                                            : null
                                    }
                                    onChange={(date) => {
                                        if (date) {
                                            // Set time to noon to avoid timezone issues
                                            date.setHours(12, 0, 0, 0);
                                        }
                                        setDateRange([dateRange[0], date]);
                                    }}
                                    dateFormat="dd.MM.yyyy"
                                    placeholderText="Izberi končni datum"
                                    className="w-full p-2 border rounded"
                                    minDate={
                                        dateRange[0]
                                            ? new Date(dateRange[0])
                                            : null
                                    }
                                    locale="sl"
                                />
                            </div>
                            {dateRange[0] && dateRange[1] && (
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatDateRange(
                                        dateRange[0]
                                            .toISOString()
                                            .split("T")[0],
                                        dateRange[1].toISOString().split("T")[0]
                                    )}
                                </p>
                            )}
                        </div>

                        <div className="md:col-span-2">
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Opomba
                            </label>
                            <input
                                type="text"
                                name="description"
                                value={newAbsence.description || ""}
                                onChange={(e) =>
                                    setNewAbsence((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                                className="w-full p-2 border rounded-md"
                                placeholder="Dodaten opis (opcijsko)"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    name="zoranOdobril"
                                    checked={newAbsence.zoranOdobril}
                                    onChange={(e) =>
                                        setNewAbsence((prev) => ({
                                            ...prev,
                                            zoranOdobril: e.target.checked,
                                        }))
                                    }
                                    className="h-4 w-4 text-[#d31245] focus:ring-[#d31245] border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700">
                                    Zoran odobril
                                </span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            className="bg-[#d31245] text-white px-4 py-2 rounded-md hover:bg-[#b30f3c] transition-colors duration-200"
                        >
                            Dodaj odsotnost
                        </button>
                    </div>
                </form>
            </div>

            {/* Month Selection and Calendar */}
            <div className="p-6 bg-white rounded-lg shadow-md">
                <MonthNavigation />
                {/* Legend */}
                <div className="p-4 mb-4 rounded-md bg-gray-50">
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                        {Object.entries(absenceTypes).map(
                            ([key, { color, label, fullName }]) => (
                                <div
                                    key={key}
                                    className="flex items-center space-x-2"
                                >
                                    <div
                                        className={`w-6 h-6 ${color} rounded flex items-center justify-center text-sm font-medium`}
                                    >
                                        {label}
                                    </div>
                                    <span className="text-sm">{fullName}</span>
                                </div>
                            )
                        )}
                        <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gray-100 rounded"></div>
                            <span className="text-sm">Vikend</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 rounded bg-red-50"></div>
                            <span className="text-sm">Praznik</span>
                        </div>
                    </div>
                </div>

                {/* Calendar Table */}
                {renderCalendarTable()}
            </div>

            {/* Annual Summary Table */}
            <div className="p-6 mt-8 bg-white rounded-lg shadow-md">
                <div className="flex flex-col space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">
                            Letni pregled odsotnosti
                        </h2>
                        <select
                            value={selectedYear}
                            onChange={(e) =>
                                setSelectedYear(parseInt(e.target.value))
                            }
                            className="p-2 border rounded-md"
                        >
                            {[...Array(5)].map((_, i) => {
                                const year = new Date().getFullYear() - 2 + i;
                                return (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Employee Selection */}
                    <div className="w-full">
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                            Izberi zaposlene za prikaz
                        </label>
                        <Select
                            isMulti
                            options={employees}
                            value={employees.filter((emp) => {
                                if (emp.isSelectAll) {
                                    return (
                                        selectedEmployeesForSummary.length ===
                                        allEmployees.length
                                    );
                                }
                                return selectedEmployeesForSummary.includes(
                                    emp.value
                                );
                            })}
                            onChange={handleEmployeeSelectionChange}
                            components={{ Option: CustomOption }}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            placeholder="Izberi zaposlene..."
                            hideSelectedOptions={false}
                            closeMenuOnSelect={false}
                        />
                    </div>

                    {/* Absence Type Selection */}
                    <div className="w-full">
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                            Izberi vrste odsotnosti za prikaz
                        </label>
                        <Select
                            isMulti
                            options={absenceTypeOptions}
                            value={absenceTypeOptions.filter((opt) =>
                                selectedAbsenceTypes.includes(opt.value)
                            )}
                            onChange={(selected) =>
                                setSelectedAbsenceTypes(
                                    selected ? selected.map((s) => s.value) : []
                                )
                            }
                            className="basic-multi-select"
                            classNamePrefix="select"
                            placeholder="Izberi vrste odsotnosti..."
                        />
                    </div>

                    {selectedEmployeesForSummary.length === 0 ? (
                        <div className="py-8 text-center text-gray-500">
                            Izberi zaposlene za prikaz letnega pregleda
                        </div>
                    ) : selectedAbsenceTypes.length === 0 ? (
                        <div className="py-8 text-center text-gray-500">
                            Izberi vrste odsotnosti za prikaz
                        </div>
                    ) : (
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th className="sticky left-0 z-10 px-4 py-2 text-sm font-medium text-left text-gray-500 border-b bg-gray-50">
                                            Ime
                                        </th>
                                        {[...Array(12)].map((_, i) => (
                                            <th
                                                key={i}
                                                className="px-2 py-2 text-sm font-medium text-center text-gray-500 border-b bg-gray-50"
                                            >
                                                {getMonthAbbreviation(i)}
                                            </th>
                                        ))}
                                        <th className="px-4 py-2 text-sm font-medium text-center text-gray-500 border-b bg-gray-50">
                                            SKUPAJ
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {Object.values(monthlyDataState).map(
                                        (employeeData) => (
                                            <tr
                                                key={employeeData.name}
                                                className="hover:bg-gray-50"
                                            >
                                                <td className="sticky left-0 px-4 py-2 text-sm font-medium text-gray-900 bg-white border-r whitespace-nowrap">
                                                    {isLower
                                                        ? employeeData.name
                                                              .split("-")[0]
                                                              .trim()
                                                        : employeeData.name}
                                                </td>
                                                {employeeData.months.map(
                                                    (month, i) => {
                                                        const types =
                                                            Object.entries(
                                                                month
                                                            )
                                                                .filter(
                                                                    ([
                                                                        _,
                                                                        days,
                                                                    ]) =>
                                                                        days > 0
                                                                )
                                                                .map(
                                                                    ([
                                                                        type,
                                                                        days,
                                                                    ]) => ({
                                                                        type,
                                                                        days,
                                                                        color: absenceTypes[
                                                                            type
                                                                        ].color,
                                                                        label: absenceTypes[
                                                                            type
                                                                        ].label,
                                                                    })
                                                                );

                                                        return (
                                                            <td
                                                                key={i}
                                                                className="px-2 py-2 text-xs text-center border-r"
                                                            >
                                                                {types.length >
                                                                0 ? (
                                                                    <div className="space-y-1">
                                                                        {types.map(
                                                                            ({
                                                                                type,
                                                                                days,
                                                                                color,
                                                                                label,
                                                                            }) => (
                                                                                <div
                                                                                    key={
                                                                                        type
                                                                                    }
                                                                                    className={`${color} rounded-sm py-0.5 px-1.5 inline-block`}
                                                                                    title={`${absenceTypes[type].fullName}: ${days} dni`}
                                                                                >
                                                                                    {
                                                                                        label
                                                                                    }

                                                                                    :
                                                                                    {
                                                                                        days
                                                                                    }
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-gray-400">
                                                                        -
                                                                    </span>
                                                                )}
                                                            </td>
                                                        );
                                                    }
                                                )}
                                                <td className="px-4 py-2 text-xs border-r">
                                                    {(() => {
                                                        const totals = {};
                                                        employeeData.months.forEach(
                                                            (month) => {
                                                                Object.entries(
                                                                    month
                                                                ).forEach(
                                                                    ([
                                                                        type,
                                                                        days,
                                                                    ]) => {
                                                                        if (
                                                                            days >
                                                                            0
                                                                        ) {
                                                                            totals[
                                                                                type
                                                                            ] =
                                                                                (totals[
                                                                                    type
                                                                                ] ||
                                                                                    0) +
                                                                                days;
                                                                        }
                                                                    }
                                                                );
                                                            }
                                                        );

                                                        const totalEntries =
                                                            Object.entries(
                                                                totals
                                                            );

                                                        return totalEntries.length >
                                                            0 ? (
                                                            <div className="space-y-1 text-left">
                                                                {totalEntries.map(
                                                                    ([
                                                                        type,
                                                                        days,
                                                                    ]) => (
                                                                        <div
                                                                            key={
                                                                                type
                                                                            }
                                                                            className={getTextColor(
                                                                                type
                                                                            )}
                                                                        >
                                                                            {
                                                                                absenceTypes[
                                                                                    type
                                                                                ]
                                                                                    .fullName
                                                                            }
                                                                            :{" "}
                                                                            {
                                                                                days
                                                                            }
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">
                                                                -
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Details Modal */}
            {selectedAbsence && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">
                                {isEditing
                                    ? "Urejanje odsotnosti"
                                    : "Podrobnosti odsotnosti"}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        {isEditing ? (
                            <div className="space-y-4">
                                <div className="relative">
                                    <label className="block mb-1 text-sm font-medium text-gray-700">
                                        Oseba
                                    </label>
                                    <Select
                                        value={employees.find(
                                            (emp) =>
                                                emp.value === editedAbsence.name
                                        )}
                                        onChange={(selected) =>
                                            setEditedAbsence((prev) => ({
                                                ...prev,
                                                name: selected?.value || "",
                                            }))
                                        }
                                        options={employees}
                                        className="basic-select"
                                        classNamePrefix="select"
                                        menuPortalTarget={document.querySelector(
                                            "body"
                                        )}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                    />
                                </div>
                                <div className="relative">
                                    <label className="block mb-1 text-sm font-medium text-gray-700">
                                        Tip odsotnosti
                                    </label>
                                    <Select
                                        value={absenceTypeOptions.find(
                                            (type) =>
                                                type.value ===
                                                editedAbsence.type
                                        )}
                                        onChange={(selected) =>
                                            setEditedAbsence((prev) => ({
                                                ...prev,
                                                type: selected?.value || "",
                                            }))
                                        }
                                        options={absenceTypeOptions}
                                        className="basic-select"
                                        classNamePrefix="select"
                                        menuPortalTarget={document.querySelector(
                                            "body"
                                        )}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">
                                        Datum
                                    </label>
                                    <div className="mt-1 space-y-2">
                                        <DatePicker
                                            selected={
                                                editedAbsence.startDate
                                                    ? new Date(
                                                          editedAbsence.startDate
                                                      )
                                                    : null
                                            }
                                            onChange={handleEditStartDateChange}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="Izberi začetni datum"
                                            className="w-full p-2 border rounded"
                                            locale="sl"
                                        />
                                        <DatePicker
                                            selected={
                                                editedAbsence.endDate
                                                    ? new Date(
                                                          editedAbsence.endDate
                                                      )
                                                    : null
                                            }
                                            onChange={handleEditEndDateChange}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="Izberi končni datum"
                                            className="w-full p-2 border rounded"
                                            minDate={
                                                editedAbsence.startDate
                                                    ? new Date(
                                                          editedAbsence.startDate
                                                      )
                                                    : null
                                            }
                                            locale="sl"
                                        />
                                    </div>
                                    {editedAbsence.startDate &&
                                        editedAbsence.endDate && (
                                            <p className="mt-1 text-sm text-gray-500">
                                                Število dni:{" "}
                                                {calculateDays(
                                                    editedAbsence.startDate,
                                                    editedAbsence.endDate
                                                )}
                                            </p>
                                        )}
                                </div>
                                <div>
                                    <label className="block mb-1 text-sm font-medium text-gray-700">
                                        Opombe
                                    </label>
                                    <textarea
                                        value={editedAbsence.description || ""}
                                        onChange={(e) =>
                                            setEditedAbsence((prev) => ({
                                                ...prev,
                                                description: e.target.value,
                                            }))
                                        }
                                        className="w-full p-2 mt-1 border rounded"
                                        rows={3}
                                        placeholder="Vnesite opombe..."
                                    />
                                </div>
                                <div>
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={editedAbsence.zoranOdobril}
                                            onChange={(e) =>
                                                setEditedAbsence((prev) => ({
                                                    ...prev,
                                                    zoranOdobril:
                                                        e.target.checked,
                                                }))
                                            }
                                            className="h-4 w-4 text-[#d31245] focus:ring-[#d31245] border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">
                                            Zoran odobril
                                        </span>
                                    </label>
                                </div>
                                <div className="flex justify-end mt-4 space-x-2">
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-100"
                                    >
                                        Prekliči
                                    </button>
                                    <button
                                        onClick={handleSaveEdit}
                                        className="px-4 py-2 bg-[#d31245] text-white rounded hover:bg-[#b30f3c]"
                                    >
                                        Shrani
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="space-y-3">
                                    <p>
                                        <span className="font-medium">
                                            Oseba:
                                        </span>{" "}
                                        {
                                            employees.find(
                                                (emp) =>
                                                    emp.value ===
                                                    selectedAbsence.name
                                            )?.label
                                        }
                                    </p>
                                    <p>
                                        <span className="font-medium">
                                            Tip:
                                        </span>{" "}
                                        {
                                            absenceTypes[selectedAbsence.type]
                                                .fullName
                                        }
                                    </p>
                                    <p>
                                        <span className="font-medium">
                                            Obdobje:
                                        </span>{" "}
                                        {formatDateRange(
                                            selectedAbsence.startDate,
                                            selectedAbsence.endDate
                                        )}
                                    </p>
                                    {selectedAbsence.description && (
                                        <p>
                                            <span className="font-medium">
                                                Opombe:
                                            </span>{" "}
                                            {selectedAbsence.description}
                                        </p>
                                    )}
                                </div>
                                <div className="flex justify-end mt-4 space-x-2">
                                    <button
                                        onClick={() =>
                                            handleDeleteDay(
                                                selectedAbsence.currentDate
                                            )
                                        }
                                        className="px-4 py-2 text-red-600 border rounded hover:bg-red-50"
                                    >
                                        Izbriši dan
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        className="px-4 py-2 text-red-600 border rounded hover:bg-red-50"
                                    >
                                        Izbriši
                                    </button>
                                    <button
                                        onClick={handleEdit}
                                        className="px-4 py-2 bg-[#d31245] text-white rounded hover:bg-[#b30f3c]"
                                    >
                                        Uredi
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OdsotnostiPage;
